import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  AttributeMapping,
  type GenericAttributeMapping,
} from '../../../../../components/AttributeMapping';
import { useLocalization } from '../../../../../hooks';
import { type RootState } from '../../../../../services/serviceTypes';
import { type StepFormMessage } from '../../../data-layer/types';
import { useFetchAttributesDataQuery } from '../../../data-store/copilotApi';
import useAddAccessAttributeMappingLogic from '../../../hooks/addAccessAttributeMappingLogic';
import ErrorBanner from '../../ErrorBanner/ErrorBanner';
import ExpandableForm from '../../ExpandableForm/ExpandableForm';
import Responses from '../../Responses/Responses';

interface AddAccessAttributeMappingProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
}

/**
 * Add access Attribute mapping
 */
function AddAccessAttributeMapping({
  message,
  onFormSubmit,
}: AddAccessAttributeMappingProps) {
  const formatMessage = useLocalization();
  const entitlementIndex = (message.formData.entitlementIndex as number) || 0;

  // Remove savingSteps dependency - buttons will be managed by form state
  const isSaving = false;

  const isEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  const { stepFormData, revertedSteps, addAccessEntitlementTemplates } =
    useSelector((state: RootState) => ({
      stepFormData: state.copilot.stepFormData,
      revertedSteps: state.copilot.revertedSteps,
      addAccessEntitlementTemplates:
        state.copilot.addAccessEntitlementTemplates,
    }));

  const {
    entitlements,
    allValidationErrors,
    applicationName,
    mandatoryMappings,
    handleMappingsChange,
    handleNext,
    createDynamicConfig,
  } = useAddAccessAttributeMappingLogic();

  const parseTemplateToMappings = useCallback(
    (
      template: { httpParams?: string } | undefined
    ): GenericAttributeMapping[] => {
      if (!template?.httpParams) {
        return [];
      }

      try {
        const httpParams = JSON.parse(template.httpParams) as {
          data?: Record<string, unknown>;
        };
        const data = httpParams.data || {};
        const mappings: GenericAttributeMapping[] = [];

        Object.entries(data).forEach(([externalField, saviyntValue], index) => {
          if (typeof saviyntValue === 'string') {
            // Handle usersObj patterns
            if (saviyntValue.includes('${usersObj.')) {
              const match = saviyntValue.match(/\$\{usersObj\.([^}]+)\}/);

              if (match) {
                mappings.push({
                  id: `mapping-${index}`,
                  saviyntField: `usersObj.${match[1]}`,
                  externalAppField: externalField,
                  saviyntValue: '',
                  externalAppValue: '',
                });
              }
            }
            // Handle entitlement_valuesObj patterns
            else if (saviyntValue.includes('${entitlement_valuesObj.')) {
              const match = saviyntValue.match(
                /\$\{entitlement_valuesObj\.([^}]+)\}/
              );

              if (match) {
                mappings.push({
                  id: `mapping-${index}`,
                  saviyntField: `entitlement_valuesObj.${match[1]}`,
                  externalAppField: externalField,
                  saviyntValue: '',
                  externalAppValue: '',
                });
              }
            }
          }
        });

        return mappings;
      } catch {
        return [];
      }
    },
    []
  );

  const getInitialMappings = useCallback((): GenericAttributeMapping[] => {
    // Get current entitlement name
    const currentEntitlementName = entitlements[entitlementIndex]?.value;

    // Get template for this index
    const currentTemplate = addAccessEntitlementTemplates[entitlementIndex] as
      | { name?: string; httpParams?: string }
      | undefined;

    // First check Redux stepFormData for saved mappings (prioritize this in edit mode)
    const addAccessStepData = stepFormData[`AddAccess_${entitlementIndex}`] as
      | { mappings?: GenericAttributeMapping[] }
      | undefined;

    if (addAccessStepData?.mappings && addAccessStepData.mappings.length > 0) {
      return addAccessStepData.mappings;
    }

    // Check if this is a newly added entitlement by comparing names (only for create mode)
    const isNewEntitlement =
      currentEntitlementName &&
      currentTemplate &&
      currentEntitlementName !== currentTemplate.name;

    // If it's a new entitlement, return empty mappings regardless of saved data
    if (isNewEntitlement) {
      return [];
    }

    // For newly added entitlements, check if template exists at the current index
    if (entitlementIndex >= addAccessEntitlementTemplates.length) {
      // No template exists for this entitlement index, return empty mappings
      return [];
    }

    // If no template exists, return empty mappings
    if (!currentTemplate) {
      return [];
    }

    const parsedMappings = parseTemplateToMappings(currentTemplate);

    return parsedMappings;
  }, [
    stepFormData,
    entitlementIndex,
    addAccessEntitlementTemplates,
    parseTemplateToMappings,
    entitlements,
  ]);

  const mappings = getInitialMappings();
  const validationError = allValidationErrors[entitlementIndex] || null;
  const currentEntitlement = entitlements[entitlementIndex]?.value || 'Unknown';

  const stepData = message.fields[0] as {
    stepName?: string;
    apiURL?: string;
    domainObjects?: string[];
    attributeDefaultValue?: string;
  };

  const queryResult = useFetchAttributesDataQuery(
    {
      apiURL: stepData.apiURL ?? '',
      domainObjects: stepData.domainObjects ?? [],
    },
    {
      skip: !stepData.apiURL || !stepData.domainObjects,
    }
  );

  const dynamicConfig = createDynamicConfig(
    queryResult.data as
      | { tableArributes?: Record<string, { name: string }[]> }
      | undefined
  );

  const handleSave = (index: number) => {
    const template = stepData.attributeDefaultValue ?? '';

    return handleNext(index, template, onFormSubmit);
  };

  return (
    <div>
      {isEditMode ? (
        <Responses type='addAccessInfoEditMode' />
      ) : (
        <Responses
          type='attributeMappingApplicationFields'
          values={[applicationName]}
        />
      )}
      <ExpandableForm
        stepName={`${stepData.stepName || 'AddAccess'}_${entitlementIndex}`}
        isCollapseable
        heading={formatMessage(
          'INTEGRATIONS.COPILOT.CURRENT_ENTITLEMENT',
          'Mapping',
          { currentEntitlement: String(currentEntitlement) }
        )}
        buttonLabel={formatMessage('INTEGRATIONS.BUTTON.SAVE', 'Save')}
        buttonkind='filled'
        entitlementIndex={entitlementIndex}
        isDisabled={isSaving}
        onSave={() => handleSave(entitlementIndex)}>
        <AttributeMapping
          key={`${entitlementIndex}-${
            revertedSteps[`AddAccess_${entitlementIndex}`] ?? 0
          }`}
          config={dynamicConfig}
          mandatoryMappings={mappings.length === 0 ? mandatoryMappings : []}
          initialMappings={mappings}
          onMappingsChange={(newMappings) => {
            handleMappingsChange(entitlementIndex, newMappings);
          }}
          showSaveButton={false}
        />
      </ExpandableForm>

      {validationError && <ErrorBanner message={validationError} />}
    </div>
  );
}

export default AddAccessAttributeMapping;
